import { logs, metrics, trace } from "@opentelemetry/api";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import {
  AggregationTemporalityPreference,
  OTLPMetricExporter,
} from "@opentelemetry/exporter-metrics-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchLogRecordProcessor, LoggerProvider } from "@opentelemetry/sdk-logs";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";
import {
  AlwaysOnSampler,
  BatchSpanProcessor,
  ParentBasedSampler,
  TracerProvider,
} from "@opentelemetry/sdk-trace";
import { OTEL } from "./attributes";

export function startClientSdk({
  service,
  version,
  sessionId,
  endpoint,
}: {
  service: string;
  version: string;
  sessionId: string;
  endpoint: (signal: "traces" | "logs" | "metrics") => string;
}) {
  const resource = resourceFromAttributes({
    [OTEL.serviceName]: service,
    [OTEL.serviceVersion]: version,
    [OTEL.sessionId]: sessionId,
    [OTEL.rumSessionId]: sessionId,
  });
  const spans = new TracerProvider({
    resource,
    sampler: new ParentBasedSampler({ root: new AlwaysOnSampler() }),
    spanProcessors: [
      new BatchSpanProcessor({ exporter: new OTLPTraceExporter({ url: endpoint("traces") }) }),
    ],
  });
  const logProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor({ exporter: new OTLPLogExporter({ url: endpoint("logs") }) }),
    ],
  });
  const meterProvider = new MeterProvider({
    resource: resourceFromAttributes({
      [OTEL.serviceName]: service,
      [OTEL.serviceVersion]: version,
      [OTEL.serviceInstanceId]: sessionId,
    }),
    readers: [
      new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({
          url: endpoint("metrics"),
          temporalityPreference: AggregationTemporalityPreference.DELTA,
        }),
        exportIntervalMillis: 30_000,
      }),
    ],
  });
  trace.setGlobalTracerProvider(spans);
  logs.setGlobalLoggerProvider(logProvider);
  metrics.setGlobalMeterProvider(meterProvider);
  return {
    resource,
    flush: () =>
      Promise.allSettled([
        spans.forceFlush(),
        logProvider.forceFlush(),
        meterProvider.forceFlush(),
      ]),
  };
}
